import { api } from '@/shared/api/client'

/**
 * Infrastructure health.
 *
 * Not a metrics feed — Prometheus answers "what is the p99". This answers the questions
 * asked at 2am while a count is running: is every service actually reachable, did the
 * migrations apply, is the database growing unexpectedly, and what has been failing.
 */

export interface ServiceHealth {
  name: string
  kind: string
  reachable: boolean
  /** A service answering in 4s is "up" in a way that matters. */
  latencyMs: number | null
  detail: string | null
}

export interface TableSize {
  table: string
  /** Planner estimate — an exact count would scan every table to draw a dashboard. */
  rows: number
  totalBytes: number
  pretty: string
}

export interface InfrastructureReport {
  services: ServiceHealth[]
  migrations: {
    currentVersion: string | null
    applied: number
    failed: number
    lastAppliedAt: string | null
    lastDescription: string | null
  }
  jvm: {
    javaVersion: string
    uptime: string
    heapUsedBytes: number
    heapMaxBytes: number
    availableProcessors: number
    liveThreads: number
  }
  largestTables: TableSize[]
  databaseBytes: number
  databasePretty: string
  totalRows: number
  integrations: {
    code: string
    category: string
    provider: string
    status: string
    health: string | null
    lastCheckedAt: string | null
    lastError: string | null
  }[]
  errors: {
    eventsLastDay: number
    failuresLastDay: number
    failureRate: number
    recent: { action: string; category: string; at: string; outcome: string }[]
  }
  generatedAt: string
}

export const infrastructureReport = () =>
  api.get<InfrastructureReport>('/infrastructure')

/**
 * Re-check one integration now.
 *
 * Needs `infrastructure:act` AND `integration:update` — the second because this action is
 * already guarded by it on the integrations screen, and a convenience button that skipped
 * that would launder one authority into another.
 */
export const checkIntegration = (code: string) =>
  api.post<InfrastructureReport>(
    `/infrastructure/integrations/${encodeURIComponent(code)}/check`)
